import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Keycloak from "keycloak-js";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
} from "react-router-dom";
import BotReportDownload from "./components/bot-report/download";
import BotReportUpload from "./components/bot-report/upload";
import ExchangeRate from "./components/exchange-rate";
import BankCostRate from "./components/bank-cost-rate";
import Header from "./components/header/Header";
import KycProfileSearch from "./components/kyc";
import TierPricing from "./components/tier-pricing";
import TierPricingHistory from "./components/tier-pricing-history";
import InternationalRemittanceReport from "./components/report/international-remittance";
import "./resources/scss/main.scss";
import "./resources/scss/report_component.scss";
import { KEYCLOAK_ROLES } from "./constant";
import mapAuthorization from "./utils/mapAuthorization";
import SpecialCustomer from "./components/special-customer";

const { keyCloakSettings, accessTokenKeyword, refreshTokenKeyword } =
  window.irsAdminSettings;

const WARNING_TIMEOUT_TIME = 13 * 60 * 1000;
const SESSION_TIMEOUT_TIME = 15 * 60 * 1000;

const events = [
  "load",
  "mousemove",
  "mousedown",
  "click",
  "scroll",
  "keypress",
];

const App = () => {
  let warnTimeout = 0;
  let logoutTimeout = 0;
  const dispatch = useDispatch();
  const [authentication, setAuthentication] = useState(false);
  const [keycloak, setKeycloak] = useState(null);
  const [username, setUsername] = useState("");
  const [userGroup, setUserGroup] = useState([]);
  const [userRoles, setUserRoles] = useState([]);

  useEffect(() => {
    const keycloak = Keycloak(keyCloakSettings);
    keycloak
      .init({
        onLoad: keyCloakSettings.onLoad,
        promiseType: keyCloakSettings.promiseType,
        checkLoginIframe: keyCloakSettings.checkLoginIframe,
      })
      .then(async (authenticated) => {
        setKeycloak(keycloak);

        // Store token in local storage for calling API.
        localStorage.setItem(
          refreshTokenKeyword,
          keycloak["refreshToken"]
        );
        localStorage.setItem(accessTokenKeyword, keycloak["token"]);
        const userInfo = await keycloak.loadUserInfo();

        dispatch({
          type: "USER/SET",
          payload: userInfo,
        });
        setUsername(userInfo.name);
        setUserGroup(userInfo.groups);
        setAuthentication(authenticated);
        setUserRoles(
          keycloak.tokenParsed.resource_access[keyCloakSettings.clientId]
            ?.roles ?? []
        );
      })
      .catch((error) => {
        console.log("Error while keycloak.init()", error);
      });

    keycloak.onTokenExpired = () => {
      keycloak
        .updateToken(10)
        .then((successful) => {
          if (!successful) {
            return;
          }
          localStorage.setItem(refreshTokenKeyword, keycloak["refreshToken"]);
          localStorage.setItem(accessTokenKeyword, keycloak["token"]);
        })
        .catch((error) => {
          console.log("Error while keycloak.onTokenExpired()", error);
        });
    };
  }, []);

  useEffect(() => {
    if (keycloak) {
      for (const event of events) {
        window.addEventListener(event, resetTimeout);
      }
      setInactivityTimeout(); // Start the timeouts after successful login
      return destroy;
    }
  }, [keycloak]);

  function setInactivityTimeout() {
    warnTimeout = window.setTimeout(warn, WARNING_TIMEOUT_TIME); // Warning popup will be shown after 13 minutes
    logoutTimeout = window.setTimeout(logout, SESSION_TIMEOUT_TIME); // User will be logged out after 15 minutes
  }

  function clearInactivityTimeout() {
    if (warnTimeout) {
      window.clearTimeout(warnTimeout);
    }
    if (logoutTimeout) {
      window.clearTimeout(logoutTimeout);
    } 
  }

  function resetTimeout() {
    clearInactivityTimeout();
    setInactivityTimeout();
  }

  function destroy() {
    clearInactivityTimeout();
    for (const event of events) {
      window.removeEventListener(event, resetTimeout);
    }
  }

  function warn() {
    alert("You will be logged out automatically in 2 minutes.");
  }

  function logout() {
    dispatch({
      type: "USER/UNSET",
    });
    keycloak.logout();
  }

  const defaultRoute = {
    "/services/irs/irs-officer": "/kyc-upload-status",
    "/services/irs/irs-administrator": "/kyc-upload-status",
    "/services/irs/irs-operator": "/kyc-upload-status",
    "/services/irs/irs-system-operation": "/kyc-upload-status",
    "/services/irs/irs-regulator": "/kyc-upload-status",
    "/services/irs/irs-exchange-rate": "/exchange-rate",
    "/services/irs/irs-cambodia-oversea": "/report/irs",
    "/services/irs/irs-myanmar-oversea": "/report/irs",
  };

  const routes = [
    {
      url: "/kyc-upload-status",
      component: <KycProfileSearch logout={logout} userGroup={userGroup} />,
      authorizeGroups: [
        "/services/irs/irs-administrator",
        "/services/irs/irs-operator",
        "/services/irs/irs-system-operation",
        "/services/irs/irs-regulator",
      ],
      authorizedRoles: [],
    },
    {
      url: "/tier-pricing",
      component: (
        <TierPricing authentication={authentication} userGroup={userGroup} />
      ),
      authorizeGroups: [
        "/services/irs/irs-administrator",
        "/services/irs/irs-operator",
        "/services/irs/irs-system-operation",
        "/services/irs/irs-regulator",
      ],
      authorizedRoles: [],
    },
    {
      url: "/tier-pricing-history",
      component: <TierPricingHistory />,
      authorizeGroups: [
        "/services/irs/irs-administrator",
        "/services/irs/irs-operator",
        "/services/irs/irs-system-operation",
        "/services/irs/irs-regulator",
      ],
      authorizedRoles: [],
    },
    {
      url: "/exchange-rate",
      component: <ExchangeRate userGroup={userGroup} />,
      authorizeGroups: ["/services/irs/irs-administrator"],
      authorizedRoles: [KEYCLOAK_ROLES.VIEW_EXCHANGE_RATE_CONF],
    },
    {
      url: "/special-customer",
      component: <SpecialCustomer userRoles={userRoles} />,
      authorizeGroups: ["/services/irs/irs-administrator"],
      authorizedRoles: [],
    },
    {
      url: "/bank-cost-rate",
      component: <BankCostRate userRoles={userRoles} />,
      authorizeGroups: [],
      authorizedRoles: [KEYCLOAK_ROLES.VIEW_BANK_COST_RATE_CONF],
    },
    {
      url: "/bot-report/download",
      component: <BotReportDownload />,
      authorizeGroups: [
        "/services/irs/irs-administrator",
        "/services/irs/irs-operator",
        "/services/irs/irs-regulator",
        "/services/irs/irs-exchange-rate",
      ],
      authorizedRoles: [],
    },
    {
      url: "/bot-report/upload",
      component: <BotReportUpload />,
      authorizeGroups: [
        "/services/irs/irs-administrator",
        "/services/irs/irs-exchange-rate",
      ],
      authorizedRoles: [],
    },
    {
      url: "/report/irs",
      component: <InternationalRemittanceReport userRoles={userRoles} />,
      authorizeGroups: [],
      authorizedRoles: [
        KEYCLOAK_ROLES.VIEW_IRS_TRANSACTION_RPT,
        KEYCLOAK_ROLES.VIEW_IRS_CAMBODIA_TRANSACTION_RPT,
        KEYCLOAK_ROLES.VIEW_IRS_MYANMAR_TRANSACTION_RPT,
      ],
    },
  ];

  if (keycloak && (userGroup.length || userRoles.length)) {
    if (authentication) {
      return (
        <div className="App">
          <Router>
            <Header
              username={username}
              logout={logout}
              userGroup={userGroup}
              userRoles={userRoles}
            />
            <div className="p-4">
              <Switch>
                <Route key="/" exact path="/">
                  <Redirect to={defaultRoute[userGroup[0]] || "/"} />
                </Route>
                {routes.map((route) => {
                  if (
                    !mapAuthorization(userGroup, route.authorizeGroups) &&
                    !mapAuthorization(userRoles, route.authorizedRoles)
                  ) {
                    return null;
                  }
                  return (
                    <Route key={route.url} exact path={route.url}>
                      {route.component}
                    </Route>
                  );
                })}
              </Switch>
            </div>
          </Router>
        </div>
      );
    }
  }

  return (
    <div
      style={{
        height: "100vh",
        width: "100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div>
        <div className="d-flex justify-content-center">
          <FontAwesomeIcon icon={faSpinner} spin={true} size="3x" />
        </div>
        <h6 className="text-lg-center" style={{ marginTop: "10px" }}>
          L O A D I N G
        </h6>
      </div>
    </div>
  );
};

export default App;
